import React, { ComponentProps, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  UseQueryStateParameters,
  urlEncode,
  useQueryState,
} from 'hooks/useQueryState';
import { useRouting } from 'hooks/useRouting';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import {
  getInvestmentTrustFundByAsset,
  getPortfolioAnalysis,
  getTargetDetail,
  getUserBroker,
} from 'config/api';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { PortfolioAnalysisLoading } from '../components/features/portfolio-analysis/share/PortfolioAnalysisLoading';
import { InvestmentTrustTabs } from '../components/features/portfolio-analysis/share/InvestmentTrustTabs';
import { PortfolioPercentageTransition } from '../components/features/portfolio-analysis/asset-information/PortfolioPercentageTransition';
import { DeviationFromTargetBalloon } from '../components/features/portfolio-analysis/asset-information/DeviationFromTargetBalloon';
import { RecommendContents } from '../components/features/portfolio-analysis/asset-information/RecommendContents';
import { PossessedItem } from '../components/features/portfolio-analysis/asset-information/PossessedItem';
import { InvestmentTrustDescription } from '../components/features/portfolio-analysis/share/InvestmentTrustDescription';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';
import {
  Account,
  PortfolioAssetType,
  PortfolioAnalysis,
  InvestmentTrustFund,
  TargetId,
  TargetModalStatus,
  TargetType,
  Target,
  ManagementType,
  InvestmentTrustType,
} from '../utils/type';
import {
  INVESTMENT_TRUST_TYPE,
  PORTFOLIO_ASSET_TYPE,
  portfolioTypeToBenchMark,
  portfolioTypeToString,
} from '../utils/portfolioInfo';
import { useBirthday } from '../utils/birthday';
import { InvestmentTrustItemListBase } from '../components/features/portfolio-analysis/investment-product/InvestmentTrustItemListBase';

export const investmentTrustTypeKey = 'selectedType' as const;

const PortfolioAnalysisAssetInformationInternal = ({
  target,
  accounts: accounts_,
  portfolioAnalysis,
  investmentTrustFund,
  type,
}: {
  target: Target;
  accounts: Account[];
  portfolioAnalysis: PortfolioAnalysis;
  investmentTrustFund: InvestmentTrustFund;
  type: PortfolioAssetType;
}) => {
  const { setIsShowMenu } = useGlobalMenuContext();

  useEffect(() => {
    return setIsShowMenu(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedInvestmentTrustTypeEncode: UseQueryStateParameters<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType']
  >['encode'] = state => {
    return urlEncode(state);
  };

  const selectedInvestmentTrustTypeDecode: UseQueryStateParameters<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType']
  >['decode'] = value => {
    switch (value) {
      case INVESTMENT_TRUST_TYPE['国内ETF'].type:
      case INVESTMENT_TRUST_TYPE['国内投信'].type:
      case INVESTMENT_TRUST_TYPE['米国ETF'].type:
        return { tag: 'Success', value };
      default:
        return { tag: 'Failed' };
    }
  };

  const [selectedInvestmentTrustType, setInvestmentTrustType] = useQueryState<
    React.ComponentProps<
      typeof InvestmentTrustTabs
    >['selectedInvestmentTrustType'],
    typeof investmentTrustTypeKey
  >(
    selectedInvestmentTrustTypeEncode,
    selectedInvestmentTrustTypeDecode,
    INVESTMENT_TRUST_TYPE['国内投信'].type,
    investmentTrustTypeKey
  );

  const accounts: ComponentProps<
    typeof InvestmentTrustItemListBase
  >['accounts'] =
    accounts_.length === 0
      ? ({ tag: 'notAggregated' } as const)
      : ({
          tag: 'aggregated',
          value: accounts_.map(account => ({
            aggregateSource: account.aggregateSource,
            brokerName: account.brokerName,
          })),
        } as const);

  const portfolio = portfolioAnalysis.itemsByPortfolioAnalysis.find(
    p => p.assetType === type
  );

  const pushRouting = (({ pushRouting }) => (path: string) => () =>
    pushRouting({ path }, true))(useRouting());

  const getDetailPath = (code: string) => `/tradedetail/${code}`;
  const possessedItems = investmentTrustFund.itemsByInvestmentType.filter(
    item => {
      return item.held;
    }
  );

  const routing = useRouting();
  const goIssueDetail = (id: TargetId, type: PortfolioAssetType) => (
    investmentTrustType?: InvestmentTrustType,
    selectedCompany?: string
  ) => {
    const investmentTrustTypeQuery = investmentTrustType
      ? `?${investmentTrustTypeKey}=${investmentTrustType}`
      : '';
    const selectedCompanyquery = selectedCompany
      ? `&company=${selectedCompany}`
      : '';
    routing.pushRouting(
      {
        path: `/target/${id}/portfolio-analysis/asset/${type}/detail${investmentTrustTypeQuery}${selectedCompanyquery}`,
      },
      true
    );
  };

  if (portfolio === null || portfolio === undefined)
    return <PortfolioAnalysisLoading />;

  const productText = () => {
    switch (portfolio.assetType) {
      case PORTFOLIO_ASSET_TYPE['外国株式(先進国+新興国)'].type:
        return (
          <>
            外国の企業へ投資します。幅広い投資先を選べるのが魅力です。全体的に値動き（リスク）が大きい傾向があります。
          </>
        );
      case PORTFOLIO_ASSET_TYPE['外国債券(先進国+新興国)'].type:
        return (
          <>
            外国の企業や国へお金を貸し付けることで一定の利子収入が得られ、満期日には額面金額が返金されることが約束されている、安全性の高い金融商品です。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.国内株式.type:
        return (
          <>
            日本の企業へ投資します。よく知っている企業、応援したい企業に投資できるのが魅力です。全体的に値動き（リスク）が大きい傾向があります。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.国内債券.type:
        return (
          <>
            日本の企業や国へお金を貸し付けることで一定の利子収入が得られ、満期日には額面金額が返金されることが約束されている、安全性の高い金融商品です。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.先進国株式.type:
        return (
          <>
            経済や技術が発展していたり、政情が安定していることや生活水準が高いのが先進国の特徴です。G7の主要国の企業を中心に投資します。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.先進国債券.type:
        return (
          <>
            先進国が発行している債券に投資します。政情が安定していることや生活水準が高いのが先進国の特徴です。G7の主要国を中心に投資します。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.新興国株式.type:
        return (
          <>
            経済水準が低い傾向はあるが、高い成長を秘めているのが特徴です。中南米、東南アジア、中東、東欧などの企業を中心に投資します。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.新興国債券.type:
        return (
          <>
            新興国が発行している債券に投資します。経済水準が低い傾向はあるが、高い成長を秘めているのが特徴です。中南米、東南アジア、中東、東欧などを中心に投資します。
          </>
        );
      case PORTFOLIO_ASSET_TYPE['REIT(国内+外国)'].type:
        return (
          <>
            投資家から資金を集め、プロが代行して複数の不動産へ投資を行います。マンション、オフィスビルなどの家賃収入や売買益が投資家へ分配されます。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.国内REIT.type:
        return (
          <>
            投資家から資金を集め、プロが代行して国内の複数の不動産へ投資を行います。マンション、オフィスビルなどの家賃収入や売買益が投資家へ分配されます。
          </>
        );
      case PORTFOLIO_ASSET_TYPE.外国REIT.type:
        return (
          <>
            投資家から資金を集め、プロが代行して海外の複数の不動産へ投資を行います。マンション、オフィスビルなどの家賃収入や売買益が投資家へ分配されます。国内REITとは違い投資法人が不動産開発も行うため利回りが高くなる傾向があります。
          </>
        );
      default:
        return <></>;
    }
  };

  const imgName = ((): string => {
    switch (portfolio.assetType) {
      case PORTFOLIO_ASSET_TYPE['外国株式(先進国+新興国)'].type:
      case PORTFOLIO_ASSET_TYPE.先進国株式.type:
        return 'foreign-stock';
      case PORTFOLIO_ASSET_TYPE['外国債券(先進国+新興国)'].type:
      case PORTFOLIO_ASSET_TYPE.先進国債券.type:
      case PORTFOLIO_ASSET_TYPE.新興国債券.type:
        return 'foreign-bond';
      case PORTFOLIO_ASSET_TYPE.国内株式.type:
        return 'stock';
      case PORTFOLIO_ASSET_TYPE.国内債券.type:
        return 'bond';
      case PORTFOLIO_ASSET_TYPE['REIT(国内+外国)'].type:
      case PORTFOLIO_ASSET_TYPE.国内REIT.type:
      case PORTFOLIO_ASSET_TYPE.外国REIT.type:
        return 'hudosan';
      case PORTFOLIO_ASSET_TYPE.新興国株式.type:
        return 'shinko';
      default:
        return 'other';
    }
  })();

  return (
    <>
      <div className="flex flex-col bg-white">
        <PortfolioPercentageTransition
          {...{ portfolio }}
          className="pl-6 pr-8 pt-4"
        />
        <DeviationFromTargetBalloon
          {...{
            estimatedPurchasePrice: investmentTrustFund.targetPurchaseAmount,
            dissociatePercentage: investmentTrustFund.dissociatePer,
          }}
          className="mt-3 mx-4"
        />
        <div className="mt-10 mx-4 text-bluegray-700 mb-6">
          <div className="font-bold text-base">
            {portfolioTypeToString(portfolio.assetType)}の特徴
          </div>
          <div
            className="mt-2 grid"
            style={{ gridTemplateColumns: 'auto 1fr' }}>
            <div style={{ aspectRatio: '1', width: '75px', height: '75px' }}>
              <img
                src={`/static/images/advisor/${imgName}.svg`}
                className=""
                alt="商品"
              />
            </div>
            <div className="ml-2 text-sm">{productText()}</div>
          </div>
        </div>
        <InvestmentTrustItemListBase
          isDetail={false}
          type={portfolio.assetType}
          items={investmentTrustFund.itemsByInvestmentType}
          onClickInvestmentTrustItem={code => pushRouting(getDetailPath(code))}
          accounts={accounts}
          selectedInvestmentTrustType={selectedInvestmentTrustType}
          setInvestmentTrustType={setInvestmentTrustType}
          TabFilteredItemsDescription={() => (
            <>
              投資対象を「
              {portfolioTypeToString(portfolio.assetType)}
              」とする金融商品のうち、過去の値動きが
              {portfolioTypeToBenchMark(portfolio.assetType)}
              に近いものを表示しています。
            </>
          )}
          headerTitle={portfolioTypeToString(portfolio.assetType)}
          goToDetail={goIssueDetail(target.id, type)}
        />
        {portfolio.assetType === PORTFOLIO_ASSET_TYPE.国内株式.type && (
          <RecommendContents />
        )}
        {possessedItems.length !== 0 && (
          <div className="mt-10 mx-4 mb-7">
            <div className="font-bold text-bluegray-700 text-lg">
              {portfolioTypeToString(portfolio.assetType)}
              へ投資している保有中の銘柄
            </div>
            <div className="mt-3 pb-4">
              {possessedItems.map((item, i, arr) => {
                return (
                  <PossessedItem
                    {...{
                      identifier: item.symbol,
                      title: item.symbolName,
                      investmentType: item.investmentType,
                      onClick: pushRouting(getDetailPath(item.symbol)),
                    }}
                    className={`${
                      i === arr.length - 1 ? 'border-b' : ''
                    } border-t border-bluegray-300`}
                    key={i}
                  />
                );
              })}
            </div>
          </div>
        )}
        <InvestmentTrustDescription type={portfolio.assetType} />
      </div>
    </>
  );
};

//MEMO: ポートフォリオTOPの金融商品押下で遷移してきたページ
const PortfolioAnalysisAssetInformation = (): React.ReactElement => {
  const { id, type } = useParams<{ id: string; type: PortfolioAssetType }>();

  const { data: target, isFetching: targetDetailIsFetching } = useQuery(
    'portfolioAnalysisAssetInformationTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: accounts, isFetching: userBrokerIsFetching } = useQuery(
    'portfolioAnalysisAssetInformationUserBroker',
    () => getUserBrokerAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: portfolioAnalysis,
    isFetching: portfolioAnalysisIsFetching,
  } = useQuery(
    'portfolioAnalysisAssetInformationPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: investmentTrustFund,
    isFetching: InvestmentTrustFundByAssetIsFetching,
  } = useQuery(
    'portfolioAnalysisAssetInformationlInvestmentTrustFundByAsset',
    () => getInvestmentTrustFundByAssetAPI(id, type),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (
    target === null ||
    target === undefined ||
    accounts === null ||
    accounts === undefined ||
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    investmentTrustFund === null ||
    investmentTrustFund === undefined ||
    targetDetailIsFetching ||
    userBrokerIsFetching ||
    portfolioAnalysisIsFetching ||
    InvestmentTrustFundByAssetIsFetching
  )
    return <PortfolioAnalysisLoading />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <PortfolioAnalysisAssetInformationInternal
        {...{
          target,
          accounts,
          portfolioAnalysis,
          investmentTrustFund,
          type,
        }}
      />
    </PremiumCheck>
  );
};

const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

const getUserBrokerAPI = async () => {
  const ret = await getUserBroker();
  if (ret === null || ret.Items === null) return null;
  return ret.Items.reduce((acc, v) => {
    return [
      ...acc,
      (item => {
        return {
          totalAssetAmount: item.TotalAssetAmount,
          account: item.Account,
          available: item.Available,
          brokerName: item.BrokerName,
          aggregateSource: item.AggregateSource,
          brokerType: item.BrokerType,
        };
      })(v),
    ];
  }, [] as Account[]);
};

const getPortfolioAnalysisAPI = async (id: TargetId) => {
  const res_ = await getPortfolioAnalysis(id);
  const res = {
    itemsByPortfolioAnalysis: res_.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: res_.RegisteredAccountName,
    targetReturnPer: res_.TargetReturnPer,
    targetReturnLimitPer: res_.TargetReturnLimitPer,
    updateDateOfAsset: res_.UpdateDateOfAsset,
    managementType: res_.ManagementType as ManagementType,
    managementItems: res_.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  };

  return res as PortfolioAnalysis;
};

const getInvestmentTrustFundByAssetAPI = async (
  id: TargetId,
  type: PortfolioAssetType
) => {
  const res_ = await getInvestmentTrustFundByAsset(id, type);
  const res = {
    itemsByInvestmentType: res_.ItemsByInvestmentType.map(item => {
      return {
        investmentType: item.InvestmentType as InvestmentTrustType,
        held: item.Held,
        oneYearReturnPer: item.OneYearReturnPer,
        salesCompany: item.SalesCompany,
        standardPrice: item.StandardPrice,
        standardPriceDate: item.StandardPriceDate,
        symbol: item.Symbol,
        symbolName: item.SymbolName,
        trustFeePer: item.TrustFeePer,
      };
    }),
    assetType: res_.AssetType as PortfolioAssetType,
    benchMark: res_.BenchMark,
    currentPer: res_.CurrentPer,
    dissociatePer: res_.DissociatePer,
    targetPer: res_.TargetPer,
    targetPurchaseAmount: res_.TargetPurchaseAmount,
  };

  return res as InvestmentTrustFund;
};

export default PortfolioAnalysisAssetInformation;
